import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import CarCard from "../components/CarCard";
import { useCarContext } from "../context/CarContext";
import { t } from "i18next";
import Banner from "../components/Banner";
import { AnimatePresence, LayoutGroup } from "framer-motion";

const CarsPage: React.FC = () => {
  const { cars } = useCarContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [carsPerPage, setCarsPerPage] = useState(4); // Valeur par défaut

  const { lang } = useParams<{ lang: "en" | "fr" | "de" }>();

  const selectedLang = ["en", "fr", "de"].includes(lang || "") ? lang! : "en";

  // ✅ Détection de la taille de la fenêtre et mise à jour du nombre de véhicules par page
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width >= 1200) {
        setCarsPerPage(4); // Grands écrans
      } else if (width >= 768) {
        setCarsPerPage(4); // Tablettes
      } else {
        setCarsPerPage(3); // Mobiles
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize); // Nettoyage
  }, []);

  const lastCarIndex = currentPage * carsPerPage;
  const firstCarIndex = lastCarIndex - carsPerPage;
  const currentCars = cars.slice(firstCarIndex, lastCarIndex);

  const totalPages = Math.ceil(cars.length / carsPerPage);

  const changePage = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  return (
    <>
      <Helmet>
        {/*  Titre et description */}
        <title>{t("cars.title")} - Race Motors Group</title>
        <meta name="description" content={t("cars.description")} />

        {/* Charset & Langue */}
        <meta charSet="UTF-8" />
        <meta httpEquiv="Content-Language" content={selectedLang} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/*  URL canonique */}
        <link
          rel="canonical"
          href={`https://www.racemotorsgroup.com/${selectedLang}/cars`}
        />

        {/* Open Graph (SEO réseaux sociaux) */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`${t("cars.title")} - Race Motors Group`}
        />
        <meta property="og:description" content={t("cars.description")} />
        <meta
          property="og:url"
          content={`https://www.racemotorsgroup.com/${selectedLang}/cars`}
        />
        <meta
          property="og:image"
          content="https://www.racemotorsgroup.com/images/seo/cars-thumbnail.jpg"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${t("cars.title")} - Race Motors Group`}
        />
        <meta name="twitter:description" content={t("cars.description")} />
        <meta
          name="twitter:image"
          content="https://www.racemotorsgroup.com/images/seo/cars-thumbnail.jpg"
        />

        {/* Schema.org JSON-LD pour SEO avancé */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CarDealer",
            name: "Race Motors Group",
            url: "https://www.racemotorsgroup.com",
            logo: "https://www.racemotorsgroup.com/images/logo.png",
            description: t("cars.description"),
            image:
              "https://www.racemotorsgroup.com/images/seo/cars-thumbnail.jpg",
            sameAs: [
              "https://www.instagram.com/racemotorsgroup",
              "https://www.facebook.com/racemotorsgroup",
              "https://www.linkedin.com/company/racemotorsgroup",
            ],
          })}
        </script>
      </Helmet>

      <section className="cars-page">
        <div className="cars-page-title">
          <h1>
            <strong>{t("cars.title")}</strong>
          </h1>
        </div>

        <div className="filtre">{/* Future filter section */}</div>

        <LayoutGroup>
          <AnimatePresence mode="wait">
            <div className="car-grid" key={selectedLang}>
              {currentCars.map((car, index) => (
                <CarCard
                  key={car.id}
                  car={car}
                  selectedLang={selectedLang}
                  index={index}
                />
              ))}
            </div>
          </AnimatePresence>
        </LayoutGroup>

        {totalPages > 1 && (
          <div className="pagination">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                className={`pagination-button ${
                  currentPage === index + 1 ? "active" : ""
                }`}
                onClick={() => changePage(index + 1)}
              >
                {index + 1}
              </button>
            ))}
          </div>
        )}
      </section>

      <Banner />
    </>
  );
};

export default CarsPage;
